import { Component, inject, Input, OnInit } from '@angular/core';
import { CompanyDetails } from '../../models/company-details';
import { AssigneeConnectionDetails } from '../../interfaces/company';
import { RecordLeadsHistory } from '../../../pages/search/models/record-leads-history';
import { LoggedInUser } from '../../models/logged-in-user';
import { AuthService } from '../../../pages/auth/services/auth.service';
import { LeadStatusValues } from '../../../pages/leads/models/lead-status';

@Component({
  selector: 'app-record-assignees',
  standalone: true,
  imports: [],
  templateUrl: './record-assignees.component.html',
  styleUrl: './record-assignees.component.scss',
})
export class RecordAssigneesComponent implements OnInit {
  @Input() recordDetails!: CompanyDetails;
  @Input() showLeadsHistoryInline = false;
  @Input() isLoadingLeadsHistory = false;
  @Input() leadsHistory: RecordLeadsHistory[] = [];
  @Input() isAdminUser = false;

  authService = inject(AuthService);

  loggedInUserInfo: LoggedInUser | null = null;

  ngOnInit(): void {
    this.loggedInUserInfo = this.authService.getLoggedInUserInfo();
  }

  isCurrentUserConnection(assignee: AssigneeConnectionDetails): boolean {
    return (
      assignee.connection_name === this.loggedInUserInfo?.connection ||
      assignee.connection_name === this.loggedInUserInfo?.username
    );
  }

  getCurrentUserAssignee(): AssigneeConnectionDetails | null {
    const assignees = this.recordDetails?.assignees || [];
    return (
      assignees.find((assignee: AssigneeConnectionDetails) =>
        this.isCurrentUserConnection(assignee),
      ) || null
    );
  }

  getSortedAssignees(): AssigneeConnectionDetails[] {
    if (!this.recordDetails?.assignees) return [];

    return [...this.recordDetails.assignees].sort((a, b) => {
      const dateA = a.assigned_on_utc
        ? new Date(a.assigned_on_utc).getTime()
        : 0;
      const dateB = b.assigned_on_utc
        ? new Date(b.assigned_on_utc).getTime()
        : 0;
      return dateB - dateA; // Latest first
    });
  }

  formatAssignmentDate(date: string | Date | undefined): string {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Invalid Date';
    }
  }

  getTotalEvents(): number {
    return this.leadsHistory?.length || 0;
  }

  getTotalAssignments(): number {
    return (
      this.leadsHistory?.filter((history) => history.status === true).length ||
      0
    );
  }

  getTotalUnassignments(): number {
    return (
      this.leadsHistory?.filter((history) => history.status === false).length ||
      0
    );
  }

  getLeadStatusLabel(leadStatus: number | undefined): string {
    if (leadStatus === undefined || leadStatus === null) return 'Not Set';

    const statusEntry = Object.entries(LeadStatusValues).find(
      ([, value]) => value === leadStatus.toString(),
    );

    if (!statusEntry) return 'Not Set';

    // Convert snake_case to Title Case
    return statusEntry[0]
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}
